import { Fragment, useState, type ReactElement } from "react";
import { useLocation, type RouteObject } from "react-router";

import { onlyQueryChanged, type Place } from "../../app/scroll-on-move.js";

import { ActivityPage } from "./activity-page.js";
import { CenterEstimatePage } from "./center-estimate-page.js";
import { ChargePage } from "./charge-page.js";
import { ContractorChangePage } from "./contractor-page.js";
import { EstimateSearchPage } from "./estimate-search-page.js";
import { LockoutPage } from "./lockout-page.js";
import { MailPage } from "./mail-page.js";
import { MailSearchPage } from "./mail-search-page.js";
import { MenuPage } from "./menu-page.js";
import { PaymentPage } from "./payment-page.js";
import { PermissionsPage } from "./permissions-page.js";
import { RestorePage } from "./restore-page.js";
import { TenantPage } from "./tenant-page.js";
import { TenantSearchPage } from "./tenant-search-page.js";

/**
 * 移るたびに画面を描き直す。
 *
 * <b>保存した後は同じ URL へ戻る</b>（権限・ロックの解除）。URL が変わらないと
 * 読み直しが走らず、<b>保存したのに一覧が古いまま</b>になる。移るたびに変わる <c>key</c> で作り直す。
 *
 * <b>クエリだけが変わったときは作り直さない</b>（検索・絞り込み・並べ替え。docs/01 §8）。
 * 作り直すと読み込み中の短い画面に替わってページが縮み、スクロールの位置が先頭へ飛び、欄のフォーカスも外れる。
 * 読み直しは API の URL が変わるので走り、届くまでは前の中身を出しておく。
 * 欄の値は探す窓が届いた条件に合わせる（<c>forms/use-search-form.ts</c>。クリアの後に前の条件を残さない）。
 */
function Fresh({ page }: { readonly page: ReactElement }): ReactElement {
    const location = useLocation();
    const [shown, setShown] = useState<{ readonly key: string; readonly place: Place; readonly drawn: string }>(
        { key: location.key, place: location, drawn: location.key });

    let { drawn } = shown;
    if (shown.key !== location.key) {
        drawn = onlyQueryChanged(shown.place, location) ? shown.drawn : location.key;
        setShown({ key: location.key, place: location, drawn });
    }
    return <Fragment key={drawn}>{page}</Fragment>;
}

const fresh = (path: string, page: ReactElement): RouteObject => ({ path, element: <Fresh page={page} /> });

/**
 * 管理センター。<b>足したら paths/admin-center.json にも足す。</b>
 *
 * <b>今のアプリが作る URL をすべて開ける</b>——契約 1 件と送ったメール 1 通は番号がパスに載る形
 * （<c>/AdminCenter/Tenant/5</c>）と、利用者を絞り込んだときのクエリの形（<c>?id=5</c>）の両方。
 */
export const adminCenterRoutes: RouteObject[] = [
    fresh("/AdminCenter", <MenuPage />),
    fresh("/AdminCenter/Index", <MenuPage />),
    fresh("/AdminCenter/Tenants", <TenantSearchPage />),
    fresh("/AdminCenter/Tenant", <TenantPage />),
    fresh("/AdminCenter/Tenant/:id", <TenantPage />),
    fresh("/AdminCenter/Tenant/:id/Contractor", <ContractorChangePage />),
    fresh("/AdminCenter/Mails", <MailSearchPage />),
    fresh("/AdminCenter/Mail", <MailPage />),
    fresh("/AdminCenter/Mail/:id", <MailPage />),
    fresh("/AdminCenter/Permissions", <PermissionsPage />),
    fresh("/AdminCenter/Lockout", <LockoutPage />),
    fresh("/AdminCenter/Restore", <RestorePage />),
    fresh("/AdminCenter/Activity", <ActivityPage />),
    fresh("/AdminCenter/Payments", <PaymentPage />),
    fresh("/AdminCenter/Charges", <ChargePage />),
    fresh("/AdminCenter/Estimates", <EstimateSearchPage />),
    fresh("/AdminCenter/Estimate/:id", <CenterEstimatePage />),
];
